import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../../store';
import { fetchWellCommentEvents } from '../../services/dashboard/WellCommentsEventService';

// Event data type
export interface EventData {
  id: number | string;
  wellId: string | number | null;
  wellGroupId?: number | null;
  categoryId: number;
  description: string;
  timestamp: string; // ISO string
  type: string; // 'Note', 'Downtime', etc.
  source?: string;
  // New API fields (optional for backward compatibility)
  Category?: string;
  Comment?: string;
  Username?: string;
  Created?: string;
}

// State interface
interface EventsState {
  events: EventData[];
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  error: string | null;
}

// Initial state
const initialState: EventsState = {
  events: [],
  status: 'idle',
  error: null,
};

// Async thunk
export const fetchPumpCheckerCommentsEvents = createAsyncThunk<
  EventData[],
  { wellId: number },
  { rejectValue: string }
>('events/fetchWellCommentEvents', async ({ wellId }, thunkAPI) => {
  try {
    const response = await fetchWellCommentEvents(wellId);
    return response;
  } catch (err: unknown) {
    return thunkAPI.rejectWithValue(err instanceof Error ? err.message : 'Failed to fetch events');
  }
});

const wellCommentsSlice = createSlice({
  name: 'events',
  initialState,
  reducers: {
    clearEvents(state) {
      state.events = [];
      state.status = 'idle';
      state.error = null;
    },
    addWellComment(state, action: PayloadAction<EventData>) {
      state.events.push(action.payload);
    },
    setWellComments(state, action: PayloadAction<EventData[]>) {
      state.events = action.payload;
      state.status = 'succeeded';
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPumpCheckerCommentsEvents.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(fetchPumpCheckerCommentsEvents.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.events = action.payload;
      })
      .addCase(fetchPumpCheckerCommentsEvents.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload ?? 'Error fetching events';
      });
  },
});

// Actions
export const { clearEvents, addWellComment, setWellComments } = wellCommentsSlice.actions;

// // Selectors
export const selectAllEvents = (state: RootState) => state.WellCommentsSlice.events;

export const selectEventsByWellId = (wellId: number) => (state: RootState) =>
  state.WellCommentsSlice.events.filter((e) => e.wellId === wellId);

export const selectEventsByDateRange = (start: string, end: string) => (state: RootState) =>
  state.WellCommentsSlice.events.filter((e) => {
    const eventDate = e['Created'] || e.timestamp;
    return eventDate && eventDate >= start && eventDate <= end;
  });

export const selectEventsStatus = (state: RootState) => state.WellCommentsSlice.status;
export const selectEventsError = (state: RootState) => state.WellCommentsSlice.error;

// Reducer
export default wellCommentsSlice.reducer;
